import _ from 'lodash';
import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import FadeIn from 'react-fade-in';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import { useStorage } from '../../hooks/use-storage';
import { useUpdateProfileModal } from '../../screens/bill/update-profile-modal';
import { usePaymentWalletModal } from '../../screens/payment/payment-wallet-modal';
import { layoutDefaults } from '../../utils/layout-defaults';
import { WalletType } from '../../utils/manager/card-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';
import { useAddressWorkflow } from '../../workflow/address-workflow';
import { useAuthWorkflow } from '../../workflow/auth-workflow';
import { usePaymentCardWorkflow } from '../../workflow/payment-card-workflow';
import { usePaymentPixWorkflow } from '../../workflow/payment-pix-workflow';
import { usePaymentSelectionWorkflow } from '../../workflow/payment-selection-workflow';
import { usePaymentWorkflow } from '../../workflow/payment-workflow';
import { PaymentButton } from '../payment/payment-button';
import { useMobileWorkflow } from '../../workflow/mobile-workflow';
import { isDevEnv } from '../../utils/manager/host-manager';

// const buttonAnimation = 'animate__animated animate__fadeInUp animate__faster animate__delay-1s';

function Buttons({
  selectedOrders,
  updateOrders,
  updating,
  setUpdating,
  onSelectInstructions,
}) {
  const [goingBack, setGoingBack] = useState(false);
  const { analyticsPageView } = useAnalytics();
  const navigate = useNavigate();
  const classes = useStyles();
  const { showUpdateProfileModal } = useUpdateProfileModal();
  const { preparePaymentWalletModal, showPaymentWalletModal } =
    usePaymentWalletModal();
  const { userRef, spot, setSpot } = useStorage();
  const { t } = useTranslation();

  // Dev test

  // const { preparePaymentWorkflow, showPaymentWorkflow } = usePaymentWorkflow();

  // const { prepareCheckoutWorkflow, showCheckoutWorkflow } =
  //   useCheckoutWorkflow();

  const { prepareAuthWorkflow, showAuthWorkflow } = useAuthWorkflow();
  const { preparePaymentSelectionWorkflow, showPaymentSelectionWorkflow } =
    usePaymentSelectionWorkflow();
  const { preparePaymentWorkflow, showPaymentWorkflow } = usePaymentWorkflow();
  const { preparePaymentCardWorkflow, showPaymentCardWorkflow } =
    usePaymentCardWorkflow();
  const { preparePaymentPixWorkflow, showPaymentPixWorkflow } =
    usePaymentPixWorkflow();
  const { showAddressWorkflow } = useAddressWorkflow();
  const { prepareMobileWorkflow, showMobileWorkflow } = useMobileWorkflow();

  async function showProfile() {
    if (!updating) return;

    setUpdating(false);
    await showUpdateProfileModal();
    analyticsPageView('Bill');

    setUpdating(true);
  }

  function goBack() {
    setGoingBack(true);

    setTimeout(() => {
      navigate(-1);
    }, 50);
  }

  return (
    <FadeIn className={classes.container}>
      <div className="d-grid">
        <PaymentButton
          selectedOrders={selectedOrders}
          updateOrders={updateOrders}
          updating={updating}
          setUpdating={setUpdating}
          onSelectInstructions={onSelectInstructions}
        />
        {!_.isEmpty(userRef.current?.token) && (
          <Button size="md" onClick={() => showProfile()} variant="secondary">
            {t('bill.button.fix-my-name')}
          </Button>
        )}
        <Button size="md" onClick={() => goBack()} variant="secondary">
          {goingBack ? <Spinner animation="border" /> : t('back')}
        </Button>

        {isDevEnv && (
          <>
            <Button
              onClick={async () => {
                await prepareMobileWorkflow();
                await showMobileWorkflow();
              }}
              variant="light"
            >
              PhoneNumberModal
            </Button>

            <Button
              onClick={async () => {
                await prepareAuthWorkflow();
                await showAuthWorkflow();
              }}
              variant="light"
            >
              AuthWorkflow
            </Button>

            <Button
              onClick={async () => {
                await prepareAuthWorkflow();
                await showAuthWorkflow({ showName: true });
              }}
              variant="light"
            >
              AuthWorkflow (name)
            </Button>

            <Button
              onClick={async () => {
                setSpot({
                  ...spot,
                  prepaid: false,
                  type: { ...spot.type, addressable: false },
                });
              }}
              variant="light"
            >
              Set Spot not Prepaid
            </Button>
            <Button
              onClick={async () => {
                setSpot({
                  ...spot,
                  prepaid: true,
                  type: { ...spot.type, addressable: false },
                });
              }}
              variant="light"
            >
              Set Spot Prepaid
            </Button>
            <Button
              onClick={async () => {
                setSpot({
                  ...spot,
                  prepaid: true,
                  type: { ...spot.type, addressable: true },
                });
              }}
              variant="light"
            >
              Set Spot Addressable
            </Button>
            <Button onClick={() => showAddressWorkflow()} variant="light">
              AddressWorkflow
            </Button>
            <Button
              onClick={async () => {
                await preparePaymentWorkflow({
                  selectedOrders,
                  updateOrders,
                });
                await showPaymentWorkflow();
              }}
              variant="light"
            >
              PayWorkflow
            </Button>
            <Button
              onClick={async () => {
                await preparePaymentSelectionWorkflow({
                  // tipAmount: 0.01,
                  selectedOrders,
                  updateOrders,
                });
                await showPaymentSelectionWorkflow();
              }}
              variant="light"
            >
              PaySelectWorkflow
            </Button>
            <Button
              onClick={async () => {
                await preparePaymentPixWorkflow({
                  orders: selectedOrders,
                  tipAmount: 0.01,
                });
                await showPaymentPixWorkflow();
              }}
              variant="light"
            >
              PixPayWorkflow
            </Button>
            <Button
              onClick={async () => {
                await preparePaymentCardWorkflow({
                  orders: selectedOrders,
                  tipAmount: 0.01,
                });
                await showPaymentCardWorkflow();
              }}
              variant="light"
            >
              CardWorkflow
            </Button>
            <Button
              onClick={async () => {
                await preparePaymentWalletModal({
                  orders: selectedOrders,
                  tipAmount: 0.01,
                  wallet: WalletType.GOOGLE_PAY,
                });
                await showPaymentWalletModal();
              }}
              variant="light"
            >
              GooglePayModal
            </Button>
            <Button
              onClick={async () => {
                await preparePaymentWalletModal({
                  orders: selectedOrders,
                  tipAmount: 0.01,
                  wallet: WalletType.APPLE_PAY,
                });
                await showPaymentWalletModal();
              }}
              variant="light"
            >
              ApplePayModal
            </Button>
          </>
        )}
      </div>
    </FadeIn>
  );
}

const useStyles = createUseStyles({
  container: {
    paddingBottom: `max(${
      layoutDefaults.verticalMargin + layoutDefaults.smallVerticalMargin
    }px, env(safe-area-inset-bottom))`,
    // marginBottom: 40,
  },
});

export { Buttons };
